// CS-2018 - Audit history popup for one EC2 instance (mirrors ReturnAuditPopup)
import React from "react";
import { Dialog, Box, Typography, IconButton, Slide, useMediaQuery, useTheme, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HistoryIcon from "@mui/icons-material/History";
import { TransitionProps } from "@mui/material/transitions";
import { useModalStyles } from "../common/useModalStyles";
import { Ec2AuditLog, Ec2Instance } from "../../redux/slice/ec2/Ec2Slice";

interface Ec2AuditPopupProps {
  open: boolean;
  onClose: () => void;
  instance: Ec2Instance | null;
  logs: Ec2AuditLog[];
  loading: boolean;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// Icon + color derived from the audit message keywords written by the backend.
const entryVisual = (log: Ec2AuditLog) => {
  const action = log.action.toUpperCase();
  if (action.startsWith("BLOCKED")) {
    return { icon: <WarningAmberIcon fontSize="small" />, color: "#FFAB00" };
  }
  if (action.includes(" STOPPED ")) {
    return { icon: <ErrorOutlineIcon fontSize="small" />, color: "#ef4444" };
  }
  if (action.includes(" STARTED ")) {
    return { icon: <CheckCircleIcon fontSize="small" />, color: "#22C55E" };
  }
  return { icon: <HistoryIcon fontSize="small" />, color: "inherit" };
};

const formatTimestamp = (value: string | number) => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const Ec2AuditPopup: React.FC<Ec2AuditPopupProps> = ({ open, onClose, instance, logs, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { tokens, paperSx, headerSx, iconBoxSx, bodySx, closeIconSx } = useModalStyles();

  if (!instance) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <HistoryIcon sx={{ color: tokens.accent, fontSize: "1.4rem" }} />
        </Box>
        <Box>
          <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
            Server Audit History
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
            {instance.name || instance.instanceId} · start/stop actions and blocked attempts
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box sx={bodySx}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} sx={{ color: tokens.accent }} />
          </Box>
        )}

        {!loading && logs.length === 0 && (
          <Typography sx={{ color: tokens.textSecondary, textAlign: "center", py: 3, fontSize: "0.9rem" }}>
            No audit history for this server yet.
          </Typography>
        )}

        {!loading &&
          logs.map((log) => {
            const { icon, color } = entryVisual(log);
            const entryColor = color === "inherit" ? tokens.textPrimary : color;
            return (
              <Box
                key={log.auditLogId}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.2,
                  p: 1.8,
                  mb: 1,
                  borderRadius: "12px",
                  backgroundColor: tokens.cardBg,
                  border: `1px solid ${tokens.border}`,
                }}
              >
                <Box sx={{ color: entryColor, display: "flex", alignItems: "center", mt: 0.2 }}>{icon}</Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: entryColor, fontWeight: 500, wordBreak: "break-word" }}
                  >
                    {log.action}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary, mt: 0.3 }}>
                    by <strong>{log.actionBy}</strong> · {formatTimestamp(log.auditTimestamp)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
      </Box>
    </Dialog>
  );
};

export default Ec2AuditPopup;
